import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Typography } from '@mui/material';
import { AssignedTask } from '../utils/TaskMaster';
import {
  checkoutAssignedTasks,
  checkoutEventAssignedTasks,
  extendAssignedTasksCheckout,
  extendEventAssignedTasksCheckout,
  releaseAssignedTasksCheckout,
  releaseEventAssignedTasksCheckout,
  updateAssignedTask,
} from '../utils/api';
import { measureAsync } from '../utils/performance';
import TaskRunner from '../components/AssignedTasks';
const TaskView = () => {
  const location = useLocation();
  const [tasks, setTasks] = useState([]);
  const [checkoutMessage, setCheckoutMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const checkoutActiveRef = useRef(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const taskId = location.state?.taskId || searchParams.get('taskId');
  const eventId = location.state?.eventId || searchParams.get('eventId');
  const runnerId = eventId || taskId;
  const checkoutTasks = eventId ? checkoutEventAssignedTasks : checkoutAssignedTasks;
  const extendCheckout = eventId
    ? extendEventAssignedTasksCheckout
    : extendAssignedTasksCheckout;
  const releaseCheckoutRequest = eventId
    ? releaseEventAssignedTasksCheckout
    : releaseAssignedTasksCheckout;

  useEffect(() => {
    let cancelled = false;
    if (runnerId) {
      setIsLoading(true);
      setCheckoutMessage(null);
      checkoutActiveRef.current = false;
      measureAsync('frontend.task_view.checkout_assigned_tasks', () => checkoutTasks(runnerId), { taskId, eventId })
        .then(data => {
          const tasks = (data.tasks || []).map(item => new AssignedTask(item));
          console.log('Fetched tasks for TaskView:', tasks);
          tasks.sort((a, b) => a.order - b.order);
          if (cancelled) {
            releaseCheckoutRequest(runnerId).catch((error) => {
              console.error('Failed to release checkout after cancelled load:', error);
            });
            return;
          }
          checkoutActiveRef.current = true;
          setTasks(tasks);
        })
        .catch(error => {
          console.error('Error fetching the task data:', error);
          if (!cancelled) {
            setTasks([]);
            setCheckoutMessage(error.data?.message || error.message || 'Unable to open task runner.');
          }
        })
        .finally(() => {
          if (!cancelled) {
            setIsLoading(false);
          }
        });
    } else {
      setIsLoading(false);
    }

    return () => {
      cancelled = true;
      if (checkoutActiveRef.current && runnerId) {
        checkoutActiveRef.current = false;
        releaseCheckoutRequest(runnerId).catch((error) => {
          console.error('Failed to release checkout:', error);
        });
      }
    };
  }, [checkoutTasks, eventId, releaseCheckoutRequest, runnerId, taskId]);

  useEffect(() => {
    if (!runnerId) return undefined;
    const intervalId = setInterval(() => {
      if (!checkoutActiveRef.current) return;
      extendCheckout(runnerId).catch((error) => {
        console.error('Failed to extend checkout:', error);
        setCheckoutMessage(error.data?.message || error.message || 'This task runner checkout expired.');
      });
    }, 60000);
    return () => clearInterval(intervalId);
  }, [extendCheckout, runnerId]);


  const handleUpdateTask = async (task) => {
    try {
      console.log('Updating task:', task.toJSON());
      const data = await measureAsync('frontend.task_view.update_assigned_task', () => updateAssignedTask(task.id, task.toJSON()), { taskId: task.id });
      console.log('Task updated successfully', data);
      return data;
    } catch (error) {
      console.error("Error:", error);
      return null;
    }
  };

  const releaseCheckout = useCallback(async () => {
    if (!checkoutActiveRef.current || !runnerId) return;
    checkoutActiveRef.current = false;
    try {
      await releaseCheckoutRequest(runnerId);
    } catch (error) {
      console.error('Failed to release checkout:', error);
    }
  }, [releaseCheckoutRequest, runnerId]);

  const handleBackToHome = async () => {
    await releaseCheckout();
    navigate('/user-home');
  };

  return (
    <Box id="task-view-page" sx={{ p: 2 }}>
      {tasks.length > 0 ? (
        <TaskRunner
          assignedTasks={tasks}
          handleUpdateTask={handleUpdateTask}
          onBackToHome={handleBackToHome}
          stopAtLevelBoundary
        />
      ) : checkoutMessage ? (
        <Box id="task-view-unavailable" sx={{ maxWidth: 520, mx: 'auto', mt: 4 }}>
          <Alert severity="info" sx={{ mb: 2 }}>{checkoutMessage}</Alert>
          <Button id="task-view-back-home" variant="contained" onClick={handleBackToHome}>Back to Home</Button>
        </Box>
      ) : isLoading ? (
        <Typography>Loading task data...</Typography>
      ) : (
        <Typography>No tasks available.</Typography>
      )}
    </Box>
  );
};

export default TaskView;
